import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  TierFeatureKey,
  hasAcademyManagementRole,
  hasClubManagementRole,
  hasRole,
  hasEntitlement,
  isSetupSupportNudgeAutoDismissed,
  type ClubOffering,
} from '@court-sync/shared';
import { useAuthStore } from '../../../store/auth';
import { useSubscription } from '../../../hooks/useSubscription';
import { useClubModuleAccess } from '../../../hooks/useClubModuleAccess';
import { useCurrentClub } from '../../../hooks/useCurrentClub';
import { useManagementDashboardFeatureAccess } from '../../../hooks/useFeatureAccess';
import { useHasMultiLocation } from '../../../hooks/useHasMultiLocation';
import { useAcademySetupProgress } from '../../../queries/setupChecklist';
import { Button, Alert, AlertDescription } from '@/components/ui';
import { DashboardStatCard } from '@/components/dashboard/DashboardStatCard';
import { useAcademies, useLessons } from '../../../hooks/useDataStore';
import { BookOpen, Calendar, CalendarDays, Banknote, Users, CalendarCheck } from 'lucide-react';
import TodaysSchedule from '../../../components/schedule/TodaysSchedule';
import LowEnrollmentSection from '../../../components/dashboard/LowEnrollmentSection';
import RecentActivity from '../../../components/dashboard/RecentActivity';
import { AcademyOwnerSetupChecklist } from '../../../components/dashboard/AcademyOwnerSetupChecklist';
import { SetupSupportNudgeCard } from '../../../components/dashboard/SetupSupportNudgeCard';
import {
  shouldShowClubSetupSupportNudge,
  shouldShowSetupSupportNudge,
} from '@/lib/setupSupportNudge';
import { ClubDashboardSection } from '../../../components/club/ClubDashboardSection';
import { buildAcademyOwnerSetupTasks } from '../../../lib/academyOwnerSetupChecklist';
import {
  buildClubOwnerSetupTasks,
  mergeAcademyAndClubSetupTasks,
} from '../../../lib/clubOwnerSetupChecklist';
import { useTour, TourAlertDialog } from '@/components/ui/Tour';
import { usePageTourActions } from '@/components/tour/PageTourHost';
import { getAcademyOwnerDashboardTourSteps } from '@/lib/academy-owner-dashboard-tour-steps';
import { getClubOwnerDashboardTourSteps } from '@/lib/club-owner-tour-steps';
import { useMatchPlayAccess } from '../../../hooks/useMatchPlayAccess';
import {
  TOUR_STEP_IDS,
} from '@/lib/tour-constants';
import { PageHeader } from '@/components/layout/PageHeader';
import { DashboardPageHelp } from '@/components/dashboard/DashboardPageHelp';
import { academyService, DashboardOverviewStats } from '../../../services/academy';
import { organizationService } from '../../../services/organization';
import { clubService } from '../../../services/club';
import { clubMembersService } from '../../../services/clubMembers';
import { clubInventoryService } from '../../../services/clubInventory';
import { courtHireService } from '../../../services/courtHire';
import { userService } from '../../../services/user';
import type { Organization } from '@court-sync/shared';

function AcademyStatSkeleton({ compact }: { compact: boolean }) {
  return (
    <span
      className={
        compact
          ? 'inline-block h-4 w-8 animate-pulse rounded bg-gray-200 dark:bg-gray-700 sm:h-5 sm:w-10'
          : 'inline-block h-5 w-12 animate-pulse rounded bg-gray-200 dark:bg-gray-700 sm:h-7 sm:w-16'
      }
    />
  );
}

function AcademyOwnerDashboardContent() {
  const { user } = useAuthStore();
  const { hasActiveSubscription, isLoading: subscriptionLoading } = useSubscription();
  const isAcademyAdminOnly = user && hasRole(user, 'academy_admin') && !hasRole(user, 'academy_owner');
  const subscriptionOkForDashboard = hasActiveSubscription || isAcademyAdminOnly;
  const { hasAccess: hasPaymentAccess } = useManagementDashboardFeatureAccess(TierFeatureKey.PAYMENT_PROCESSING);
  const { hasAccess: hasLowEnrollmentAccess } = useManagementDashboardFeatureAccess(TierFeatureKey.LOW_ENROLLMENT_MGMT);
  const { hasAccess: hasCoachPortalAccess } = useManagementDashboardFeatureAccess(TierFeatureKey.COACH_PORTAL);

  // Use centralized stores
  const { currentAcademy, isLoading: academiesLoading, loadAcademy } = useAcademies();
  const { lessons } = useLessons(user?.academyId);
  const { data: setupProgress, isLoading: setupProgressLoading } = useAcademySetupProgress(
    user?.academyId
  );
  const hasMultiLocation = useHasMultiLocation();
  const { hasClubManagement, entitlements, isLoading: clubEntitlementsLoading } =
    useClubModuleAccess();
  const { hasAccess: hasMatchPlay, isLoading: matchPlayLoading } = useMatchPlayAccess();
  const { currentClub } = useCurrentClub();

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [clubCourtsCount, setClubCourtsCount] = useState(0);
  const [orgSportsCount, setOrgSportsCount] = useState(0);
  const [membershipPlansCount, setMembershipPlansCount] = useState(0);
  const [inventoryItemsCount, setInventoryItemsCount] = useState(0);
  const [courtHireRatesCount, setCourtHireRatesCount] = useState(0);
  const [clubSetupDataLoading, setClubSetupDataLoading] = useState(false);

  const [dashboardStats, setDashboardStats] = useState<DashboardOverviewStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const { startWithSteps, close: closeDashboardTour, isActive: isDashboardTourActive } = useTour();
  const { beginDashboardTourSession } = usePageTourActions();
  const [showTourDialog, setShowTourDialog] = useState(false);
  const [checklistDismissing, setChecklistDismissing] = useState(false);
  const [supportNudgeDismissing, setSupportNudgeDismissing] = useState(false);
  const [clubSupportNudgeDismissing, setClubSupportNudgeDismissing] = useState(false);

  // Load current academy if user has academyId
  useEffect(() => {
    if (user?.academyId && !currentAcademy) {
      loadAcademy(user.academyId);
    }
  }, [user?.academyId, currentAcademy, loadAcademy]);

  const fetchDashboardStats = useCallback(async () => {
    if (!user?.academyId) return;
    try {
      setStatsLoading(true);
      const response = await academyService.getDashboardStats(user.academyId);
      if (response.success && response.data) {
        setDashboardStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, [user?.academyId]);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  const hasAcademySection = Boolean(user && hasAcademyManagementRole(user));

  const buildDashboardTourSteps = useCallback(() => {
    if (!user) return [];

    const isAcademyOperator = hasAcademyManagementRole(user);
    if (!isAcademyOperator) {
      return getClubOwnerDashboardTourSteps();
    }

    const isAcademyOwner = hasRole(user, 'academy_owner');
    const hasClubRole = hasClubManagementRole(user);
    const canOperateClub = hasClubRole || isAcademyOwner;
    const hasClubModule = canOperateClub && hasClubManagement && !clubEntitlementsLoading;

    return getAcademyOwnerDashboardTourSteps({
      hasAcademyManagement: true,
      hasClubModule,
      hasClubRole,
      canOperateClub,
      isAcademyOwner,
      isClubOwner: hasRole(user, 'club_owner'),
      hasMatchPlay: hasMatchPlay && !matchPlayLoading,
      hasCoachPortalAccess,
    });
  }, [
    user,
    hasClubManagement,
    clubEntitlementsLoading,
    hasMatchPlay,
    matchPlayLoading,
    hasCoachPortalAccess,
  ]);

  const launchDashboardTour = useCallback(() => {
    if (isDashboardTourActive) return;
    const steps = buildDashboardTourSteps();
    if (steps.length === 0) return;
    startWithSteps(steps);
  }, [buildDashboardTourSteps, isDashboardTourActive, startWithSteps]);
  const showClubOverview =
    (hasClubManagementRole(user) || hasAcademyManagementRole(user)) && hasClubManagement;

  useEffect(() => {
    if (!showClubOverview) return undefined;
    let cancelled = false;

    const loadClubSetupData = async () => {
      setClubSetupDataLoading(true);
      try {
        const orgRes = await organizationService.getMyOrganization();
        if (!cancelled && orgRes.success && orgRes.data) {
          setOrganization(orgRes.data);
          const sportsRes = await organizationService.getOrganizationSports(orgRes.data.id);
          if (sportsRes.success && sportsRes.data) {
            setOrgSportsCount(sportsRes.data.filter((sport) => sport.isActive).length);
          }
        }

        if (currentClub?.id) {
          const [courtsRes, plansRes, inventoryRes, ratesRes] = await Promise.all([
            clubService.getFacilityCourts(currentClub.id),
            clubMembersService.listMembershipTypes(currentClub.id).catch(() => ({ success: false, data: [] })),
            clubInventoryService.listItems(currentClub.id).catch(() => ({ success: false, data: [] })),
            courtHireService.getRates(currentClub.id).catch(() => ({ success: false, data: [] })),
          ]);
          if (!cancelled) {
            setClubCourtsCount(courtsRes.success && courtsRes.data ? courtsRes.data.length : 0);
            setMembershipPlansCount(
              plansRes.success && plansRes.data ? plansRes.data.length : 0
            );
            setInventoryItemsCount(
              inventoryRes.success && inventoryRes.data ? inventoryRes.data.length : 0
            );
            setCourtHireRatesCount(
              ratesRes.success && ratesRes.data ? ratesRes.data.length : 0
            );
          }
        }
      } catch (error) {
        console.error('Failed to load club setup checklist data:', error);
      } finally {
        if (!cancelled) {
          setClubSetupDataLoading(false);
        }
      }
    };

    void loadClubSetupData();
    return () => {
      cancelled = true;
    };
  }, [showClubOverview, currentClub?.id]);

  const dashboardTourPreferences = hasAcademySection
    ? ({ tourCompletedAcademyOwnerDashboard: true } as const)
    : ({ tourCompletedClubOwnerDashboard: true } as const);
  const dashboardTourCompleted = hasAcademySection
    ? user?.preferences?.tourCompletedAcademyOwnerDashboard ?? false
    : user?.preferences?.tourCompletedClubOwnerDashboard ?? false;

  useEffect(() => {
    if (dashboardTourCompleted || !user) return undefined;
    const timer = window.setTimeout(() => setShowTourDialog(true), 500);
    return () => window.clearTimeout(timer);
  }, [dashboardTourCompleted, user]);

  const activeLessons = lessons.filter((lesson) => lesson.isActive);

  const setupTasks = useMemo(() => {
    if (!currentAcademy || !user?.academyId || !setupProgress?.done) {
      return [];
    }
    return buildAcademyOwnerSetupTasks({
      academy: currentAcademy,
      done: setupProgress.done,
      hasPaymentProcessingFeature: hasPaymentAccess,
      hasMultiLocationFeature: hasMultiLocation,
      operatingProfile: currentAcademy.operatingProfile,
    });
  }, [
    currentAcademy,
    user?.academyId,
    setupProgress?.done,
    hasPaymentAccess,
    hasMultiLocation,
  ]);

  const clubOfferings = useMemo((): ClubOffering[] => {
    if (!entitlements) {
      return ['court_hire'];
    }
    const offerings: ClubOffering[] = [];
    if (hasEntitlement(entitlements, TierFeatureKey.COURT_HIRE)) {
      offerings.push('court_hire');
    }
    if (hasEntitlement(entitlements, TierFeatureKey.MEMBERSHIPS)) {
      offerings.push('memberships');
    }
    if (hasEntitlement(entitlements, TierFeatureKey.INVENTORY_HIRE)) {
      offerings.push('inventory_hire');
    }
    return offerings.length > 0 ? offerings : ['court_hire'];
  }, [entitlements]);

  const combinedOverview = showClubOverview && hasAcademySection;

  const setupDataLoading = academiesLoading || setupProgressLoading;
  const checklistDismissed = user?.preferences?.academyOwnerSetupChecklistDismissed ?? false;
  const checklistCompletedAt = setupProgress?.completedAt ?? null;

  const clubSetupTasks = useMemo(() => {
    if (!showClubOverview) {
      return [];
    }
    return buildClubOwnerSetupTasks({
      organization,
      club: currentClub,
      courtsCount: clubCourtsCount,
      activeSportsCount: orgSportsCount,
      membershipPlansCount,
      inventoryItemsCount,
      courtHireRatesCount,
      offerings: clubOfferings,
      hasPaymentProcessing: hasPaymentAccess || hasClubManagement,
    });
  }, [
    showClubOverview,
    organization,
    currentClub,
    clubCourtsCount,
    orgSportsCount,
    membershipPlansCount,
    inventoryItemsCount,
    courtHireRatesCount,
    clubOfferings,
    hasPaymentAccess,
    hasClubManagement,
  ]);

  const displaySetupTasks = useMemo(() => {
    if (!hasAcademySection) {
      return clubSetupTasks;
    }
    if (!combinedOverview) {
      return setupTasks;
    }
    return mergeAcademyAndClubSetupTasks(setupTasks, clubSetupTasks);
  }, [hasAcademySection, combinedOverview, setupTasks, clubSetupTasks]);

  const checklistDataLoading = hasAcademySection
    ? setupDataLoading || (combinedOverview && clubSetupDataLoading)
    : clubSetupDataLoading;

  const allSetupComplete =
    displaySetupTasks.length > 0 && displaySetupTasks.every((task) => task.done);

  const showSetupChecklist =
    hasAcademySection &&
    Boolean(user?.academyId) &&
    hasActiveSubscription &&
    !subscriptionLoading &&
    Boolean(currentAcademy) &&
    !checklistDismissed &&
    !checklistDataLoading &&
    !allSetupComplete;

  const showSupportNudge = shouldShowSetupSupportNudge({
    hasAcademySection,
    hasActiveSubscription,
    subscriptionLoading,
    hasCurrentAcademy: Boolean(currentAcademy),
    setupTaskCount: displaySetupTasks.length,
    allSetupComplete,
    checklistDismissed,
    checklistCompletedAt,
    nudgeDismissed: Boolean(user?.preferences?.setupSupportNudgeDismissed),
    showSetupChecklist,
  });

  useEffect(() => {
    if (
      !allSetupComplete ||
      !checklistCompletedAt ||
      user?.preferences?.setupSupportNudgeDismissed ||
      !isSetupSupportNudgeAutoDismissed(checklistCompletedAt)
    ) {
      return;
    }

    void (async () => {
      try {
        await userService.updatePreferences({ setupSupportNudgeDismissed: true });
        const cur = useAuthStore.getState().user;
        if (cur) {
          useAuthStore.setState({
            user: {
              ...cur,
              preferences: { ...cur.preferences, setupSupportNudgeDismissed: true },
            },
          });
        }
      } catch (error) {
        console.error('Failed to persist auto-dismissed setup support nudge:', error);
      }
    })();
  }, [
    allSetupComplete,
    checklistCompletedAt,
    user?.preferences?.setupSupportNudgeDismissed,
  ]);

  const handleDismissSetupChecklist = async () => {
    setChecklistDismissing(true);
    try {
      await userService.updatePreferences({
        academyOwnerSetupChecklistDismissed: true,
      });
      const cur = useAuthStore.getState().user;
      if (cur) {
        useAuthStore.setState({
          user: {
            ...cur,
            preferences: { ...cur.preferences, academyOwnerSetupChecklistDismissed: true },
          },
        });
      }
    } catch (error) {
      console.error('Failed to dismiss setup checklist:', error);
      toast.error('Could not hide the checklist. Try again.');
    } finally {
      setChecklistDismissing(false);
    }
  };

  const handleReplayDashboardTour = useCallback(() => {
    launchDashboardTour();
  }, [launchDashboardTour]);

  const handleDismissSupportNudge = async () => {
    setSupportNudgeDismissing(true);
    try {
      await userService.updatePreferences({
        setupSupportNudgeDismissed: true,
      });
      const cur = useAuthStore.getState().user;
      if (cur) {
        useAuthStore.setState({
          user: {
            ...cur,
            preferences: { ...cur.preferences, setupSupportNudgeDismissed: true },
          },
        });
      }
    } catch (error) {
      console.error('Failed to dismiss setup support nudge:', error);
      toast.error('Could not dismiss the suggestion. Try again.');
    } finally {
      setSupportNudgeDismissing(false);
    }
  };

  const clubChecklistDismissed = user?.preferences?.clubOwnerSetupChecklistDismissed ?? false;
  const allClubSetupComplete =
    clubSetupTasks.length > 0 && clubSetupTasks.every((task) => task.done);
  const showClubSetupChecklist =
    showClubOverview &&
    !hasAcademySection &&
    hasActiveSubscription &&
    !subscriptionLoading &&
    !clubChecklistDismissed &&
    !clubSetupDataLoading &&
    !allClubSetupComplete;

  const clubChecklistCompletedAt =
    user?.preferences?.clubOwnerSetupChecklistCompletedAt ?? null;

  const showClubSupportNudge = shouldShowClubSetupSupportNudge({
    hasClubSection: showClubOverview && !hasAcademySection,
    hasActiveSubscription,
    subscriptionLoading,
    hasCurrentClub: Boolean(currentClub),
    clubSetupTaskCount: clubSetupTasks.length,
    allClubSetupComplete,
    clubChecklistDismissed,
    clubChecklistCompletedAt,
    clubNudgeDismissed: Boolean(user?.preferences?.clubSetupSupportNudgeDismissed),
    showClubSetupChecklist,
  });

  useEffect(() => {
    if (!allClubSetupComplete || clubChecklistCompletedAt || !showClubOverview) {
      return;
    }

    const completedAt = new Date().toISOString();
    void (async () => {
      try {
        await userService.updatePreferences({
          clubOwnerSetupChecklistCompletedAt: completedAt,
        });
        const cur = useAuthStore.getState().user;
        if (cur) {
          useAuthStore.setState({
            user: {
              ...cur,
              preferences: {
                ...cur.preferences,
                clubOwnerSetupChecklistCompletedAt: completedAt,
              },
            },
          });
        }
      } catch (error) {
        console.error('Failed to persist club setup checklist completion:', error);
      }
    })();
  }, [allClubSetupComplete, clubChecklistCompletedAt, showClubOverview]);

  useEffect(() => {
    if (
      !allClubSetupComplete ||
      !clubChecklistCompletedAt ||
      user?.preferences?.clubSetupSupportNudgeDismissed ||
      !isSetupSupportNudgeAutoDismissed(clubChecklistCompletedAt)
    ) {
      return;
    }

    void (async () => {
      try {
        await userService.updatePreferences({ clubSetupSupportNudgeDismissed: true });
        const cur = useAuthStore.getState().user;
        if (cur) {
          useAuthStore.setState({
            user: {
              ...cur,
              preferences: { ...cur.preferences, clubSetupSupportNudgeDismissed: true },
            },
          });
        }
      } catch (error) {
        console.error('Failed to persist auto-dismissed club setup support nudge:', error);
      }
    })();
  }, [
    allClubSetupComplete,
    clubChecklistCompletedAt,
    user?.preferences?.clubSetupSupportNudgeDismissed,
  ]);

  const handleDismissClubSetupChecklist = async () => {
    setChecklistDismissing(true);
    try {
      await userService.updatePreferences({
        clubOwnerSetupChecklistDismissed: true,
      });
      const cur = useAuthStore.getState().user;
      if (cur) {
        useAuthStore.setState({
          user: {
            ...cur,
            preferences: { ...cur.preferences, clubOwnerSetupChecklistDismissed: true },
          },
        });
      }
    } catch (error) {
      console.error('Failed to dismiss club setup checklist:', error);
      toast.error('Could not hide the checklist. Try again.');
    } finally {
      setChecklistDismissing(false);
    }
  };

  const handleDismissClubSupportNudge = async () => {
    setClubSupportNudgeDismissing(true);
    try {
      await userService.updatePreferences({
        clubSetupSupportNudgeDismissed: true,
      });
      const cur = useAuthStore.getState().user;
      if (cur) {
        useAuthStore.setState({
          user: {
            ...cur,
            preferences: { ...cur.preferences, clubSetupSupportNudgeDismissed: true },
          },
        });
      }
    } catch (error) {
      console.error('Failed to dismiss club setup support nudge:', error);
      toast.error('Could not dismiss the suggestion. Try again.');
    } finally {
      setClubSupportNudgeDismissing(false);
    }
  };

  if (!user) {
    return null;
  }

  const statSize = combinedOverview ? 'compact' : 'default';

  const academyOverviewSection = hasAcademySection ? (
    <section
      className={combinedOverview ? undefined : 'mb-8'}
      id={TOUR_STEP_IDS.BUSINESS_OVERVIEW}
      aria-labelledby="academy-overview-heading"
    >
      <div className={`flex flex-wrap items-center justify-between gap-2 ${combinedOverview ? 'mb-2' : 'mb-4'}`}>
        <h2
          id="academy-overview-heading"
          className={
            combinedOverview
              ? 'text-base font-semibold text-gray-900 dark:text-white'
              : 'text-lg font-semibold text-gray-900 dark:text-white'
          }
        >
          Academy overview
        </h2>
        {dashboardStats?.current.period.label ? (
          <span
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400"
            title={
              dashboardStats.current.period.kind === 'term'
                ? 'Includes term group lessons plus camps and private lessons running during this term'
                : 'Includes all lesson types with activity this month'
            }
          >
            <CalendarDays className="h-4 w-4 shrink-0" aria-hidden />
            {dashboardStats.current.period.label}
          </span>
        ) : null}
      </div>
      <div
        className={
          combinedOverview
            ? 'grid grid-cols-2 gap-2 sm:gap-3'
            : 'grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4'
        }
      >
        <DashboardStatCard
          size={statSize}
          icon={<Banknote aria-hidden />}
          iconBg="bg-tennis-100 dark:bg-tennis-900"
          iconColor="text-tennis-600 dark:text-tennis-400"
          label="Revenue"
          value={
            statsLoading ? (
              <AcademyStatSkeleton compact={combinedOverview} />
            ) : (
              `$${(dashboardStats?.current.revenuePotential ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
            )
          }
        />
        <DashboardStatCard
          size={statSize}
          icon={<BookOpen aria-hidden />}
          iconBg="bg-primary-100 dark:bg-primary-900"
          iconColor="text-primary-600 dark:text-primary-400"
          label="Lessons"
          value={
            statsLoading ? (
              <AcademyStatSkeleton compact={combinedOverview} />
            ) : (
              dashboardStats?.current.lessons ?? 0
            )
          }
        />
        <DashboardStatCard
          size={statSize}
          icon={<Users aria-hidden />}
          iconBg="bg-primary-100 dark:bg-primary-900"
          iconColor="text-primary-600 dark:text-primary-400"
          label="Students"
          value={
            statsLoading ? (
              <AcademyStatSkeleton compact={combinedOverview} />
            ) : (
              dashboardStats?.current.students ?? 0
            )
          }
        />
        <DashboardStatCard
          size={statSize}
          icon={<CalendarCheck aria-hidden />}
          iconBg="bg-tennis-100 dark:bg-tennis-900"
          iconColor="text-tennis-600 dark:text-tennis-400"
          label="Bookings"
          value={
            statsLoading ? (
              <AcademyStatSkeleton compact={combinedOverview} />
            ) : (
              dashboardStats?.current.bookings ?? 0
            )
          }
        />
      </div>
    </section>
  ) : null;

  return (
    <>
    <div className="contents">
      <PageHeader
        title="Dashboard"
        subtitle={
          hasAcademySection
            ? 'Welcome to your management dashboard'
            : 'Facility and club operations overview'
        }
        help={
          <DashboardPageHelp
            title={
              hasAcademySection
                ? 'Welcome to your management dashboard'
                : 'Facility and club operations overview'
            }
            description={
              hasAcademySection
                ? 'See today’s schedule, track setup tasks, and jump to lessons, students, payments, and club tools from the sidebar.'
                : 'See today’s schedule, track setup tasks, and jump to court hire, memberships, and club settings from the sidebar.'
            }
            academyId={user?.academyId}
            onReplayTour={handleReplayDashboardTour}
          />
        }
      />

      {!subscriptionLoading && !subscriptionOkForDashboard && (hasAcademySection || (showClubOverview && !hasAcademySection)) && (
        <Alert className="mb-6 border-blue-300 bg-blue-50">
          <Calendar className="h-5 w-5 text-blue-600" />
          <AlertDescription>
            <div className="flex items-center justify-between">
              <div>
                <strong className="text-blue-800">
                  No Active Subscription
                </strong>
                <p className="text-blue-700 mt-1">
                  {hasAcademySection
                    ? 'Subscribe to access all features and start managing your academy.'
                    : 'Subscribe to access all features and start managing your club.'}
                </p>
              </div>
              <Link to="/settings?tab=subscription">
                <Button size="sm" variant="outline" className="border-blue-600 text-blue-800 hover:bg-blue-100">
                  View Plans
                </Button>
              </Link>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {showSetupChecklist && (
        <AcademyOwnerSetupChecklist
          tasks={displaySetupTasks}
          onDismiss={handleDismissSetupChecklist}
          dismissing={checklistDismissing}
          tourAnchorId={TOUR_STEP_IDS.SETUP_CHECKLIST}
        />
      )}

      {showSupportNudge && (
        <SetupSupportNudgeCard
          academyId={user.academyId}
          onDismiss={handleDismissSupportNudge}
          dismissing={supportNudgeDismissing}
          tourAnchorId={TOUR_STEP_IDS.SETUP_CHECKLIST}
        />
      )}

      {showClubSetupChecklist && (
        <AcademyOwnerSetupChecklist
          tasks={clubSetupTasks}
          onDismiss={handleDismissClubSetupChecklist}
          dismissing={checklistDismissing}
          tourAnchorId={TOUR_STEP_IDS.SETUP_CHECKLIST}
        />
      )}

      {showClubSupportNudge && (
        <SetupSupportNudgeCard
          onDismiss={handleDismissClubSupportNudge}
          dismissing={clubSupportNudgeDismissing}
          tourAnchorId={TOUR_STEP_IDS.SETUP_CHECKLIST}
        />
      )}

      {combinedOverview ? (
        <div className="mb-8 grid gap-4 lg:grid-cols-2 lg:gap-6">
          <ClubDashboardSection compact />
          {academyOverviewSection}
        </div>
      ) : (
        <>
          <ClubDashboardSection />
          {academyOverviewSection}
        </>
      )}

      {hasAcademySection &&
        currentAcademy &&
        hasLowEnrollmentAccess &&
        (currentAcademy.lowEnrollmentAlertsEnabled ??
          ((currentAcademy.minimumStudentsForLesson ?? 0) > 0)) && (
        <div className="mb-8">
          <LowEnrollmentSection
            academyId={currentAcademy.id}
            lessons={activeLessons}
          />
        </div>
      )}

      {(hasAcademySection || showClubOverview) && (
        <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div id={TOUR_STEP_IDS.TODAYS_SCHEDULE}>
            <TodaysSchedule />
          </div>
          <RecentActivity
            academyId={hasAcademySection ? currentAcademy?.id ?? user.academyId ?? undefined : undefined}
            clubId={!hasAcademySection ? currentClub?.id : undefined}
            cardId={TOUR_STEP_IDS.RECENT_ACTIVITY}
          />
        </div>
      )}

      {(hasAcademySection || showClubOverview) && (
        <TourAlertDialog
          isOpen={showTourDialog && !dashboardTourCompleted}
          setIsOpen={setShowTourDialog}
          title="Welcome to CourtSync!"
          description={
            combinedOverview
              ? "Discover how to get the most out of your academy and club dashboards. We'll show you the essentials in just a few steps."
              : hasAcademySection
                ? "Discover how to get the most out of your academy dashboard. We'll show you the essentials in just a few steps."
                : "Discover how to get the most out of your club management dashboard. We'll show you the essentials in just a few steps."
          }
          onConfirm={() => {
            beginDashboardTourSession(dashboardTourPreferences);
            launchDashboardTour();
          }}
          onSkip={() => {
            beginDashboardTourSession(dashboardTourPreferences);
            closeDashboardTour();
          }}
        />
      )}
    </div>
    </>
  );
}

export default function AcademyOwnerDashboard() {
  return <AcademyOwnerDashboardContent />;
}
